/** Remastered fireflies, butterflies, amoeba and slime. Pure. */

import type { Ramp, RampSet, RGB } from './color';
import { rampOf } from './color';
import { Px } from './px';
import { hash3, lambert, normalize, step, tileNoise } from './shade';

const HOT: RGB = [255, 250, 230];

/** The living things keep their own colours in every cave, so they are never mistaken for rock. */
const AMOEBA: Ramp = rampOf([74, 176, 64]);
const SLIME: Ramp = rampOf([46, 166, 158]);

/** A glowing square of embers spinning a quarter-turn over four frames, white-hot at its heart. */
export function firefly(r: RampSet, k: number): Px {
  const p = new Px();
  const a = (k * Math.PI) / 8;
  const cos = Math.cos(a);
  const sin = Math.sin(a);
  for (let y = 0; y < 16; y++) {
    for (let x = 0; x < 16; x++) {
      const dx = x + 0.5 - 8;
      const dy = y + 0.5 - 8;
      const u = dx * cos + dy * sin;
      const v = -dx * sin + dy * cos;
      const m = Math.max(Math.abs(u), Math.abs(v));
      if (m <= 1.7) p.set(x, y, HOT);
      else if (m >= 3.4 && m <= 6.4) {
        const lit = u + v < -2 ? 4 : u + v < 3 ? 3 : 2;
        p.set(x, y, (m > 5.2 ? r.earth : r.bright)[lit] ?? r.bright[3]);
      }
    }
  }
  p.outline(r.ink);
  return p;
}

/** Four lit wings with eye-spots round a slim body; frame k folds them in and out. */
export function butterfly(r: RampSet, k: number): Px {
  const f = [1, 0.68, 0.36, 0.68][k & 3] ?? 1;
  const p = new Px();
  for (const side of [-1, 1]) {
    p.dome(8 + side * 4.1 * f, 6.2, Math.max(1, 3.9 * f), 3.5, r.rock, 1, 4);
    p.dome(8 + side * 3 * f, 11.2, Math.max(0.9, 2.7 * f), 2.5, r.rock, 0, 3);
    if (f > 0.5) {
      const ex = Math.round(8 + side * 4.3 * f - 0.5);
      p.set(ex, 6, r.bright[4]);
      p.set(ex, 7, r.bright[2]);
    }
  }
  p.bar(7, 3, 8, 13, r.earth, 0, 2);
  p.set(6, 2, r.ink);
  p.set(9, 2, r.ink);
  p.set(5, 1, r.bright[3]);
  p.set(10, 1, r.bright[3]);
  p.outline(r.ink);
  return p;
}

/** A living mass: two layers of lumps drifting against each other, bubbles catching the light. */
export function amoeba(_r: RampSet, k: number): Px {
  const p = new Px();
  const h = (x: number, y: number): number => tileNoise(x + k * 4, y, 4, 4, 40) * 0.55 + tileNoise(x, y - k * 4, 4, 4, 41) * 0.45;
  for (let y = 0; y < 16; y++) {
    for (let x = 0; x < 16; x++) {
      const v = h(x, y);
      if (v < 0.28) {
        p.set(x, y, AMOEBA[0]);
        continue;
      }
      if (v > 0.8) {
        p.set(x, y, v > 0.86 ? HOT : AMOEBA[4]);
        continue;
      }
      const n = normalize([(h(x - 1, y) - h(x + 1, y)) * 4, (h(x, y - 1) - h(x, y + 1)) * 4, 1]);
      p.set(x, y, AMOEBA[step(lambert(n) * 0.85 + (v - 0.5) * 0.4, x, y, 0, 3)] ?? AMOEBA[2]);
    }
  }
  return p;
}

/** Glossy teal ooze: a wavering sheen along the top, bright drips sliding down. */
export function slime(_r: RampSet, k: number): Px {
  const p = new Px();
  const r = { rock: SLIME, bright: SLIME };
  for (let y = 0; y < 16; y++) {
    for (let x = 0; x < 16; x++) {
      const wave = 1 + Math.round(Math.sin((x + k * 4) * 0.8) * 0.8);
      let c = y < wave ? r.rock[1] : y === wave ? r.rock[4] : y === wave + 1 ? r.rock[3] : r.rock[y > 11 ? 1 : 2];
      if (y > wave + 1 && hash3(x, y, 51) % 11 === 0) c = r.rock[1];
      p.set(x, y, c);
    }
  }
  for (const x of [3, 8, 12]) {
    const drip = (hash3(x, 0, 52) % 16 + k * 4) % 16;
    if (drip < 4) continue;
    p.set(x, drip, r.bright[4]);
    p.set(x, drip - 1, r.bright[2]);
    p.set(x, drip - 2, r.rock[3]);
  }
  return p;
}
